import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  QueryFunctionContext,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import {add, format, formatISO, parseISO, parseJSON} from 'date-fns';
import toast from 'react-hot-toast';

// import useUi from 'hooks/useUi';
import {ListResponse, Order, Vehicle, RouteStatus, Driver} from 'types';
import api from 'api';
import {wait} from 'utils/dummy';

import useStore from './store';
import {useHistory, useLocation} from 'react-router-dom';
import useOrderList from 'hooks/useOrderList';
// import {orderSortFunctionMap} from 'utils/order-helpers';
import {get, reject} from 'lodash';
import {FilterOption} from './components/Filter/Filter';
import useDebounce from 'hooks/useDebounce';
import {useForm} from 'react-hook-form';

export interface Route {
  id: number | string;
  driver?: Driver;
  vehicle?: Vehicle;
  start_time: string | undefined;
  start_location: string;
  end_location: string;
  orders: Order[];
  deliveries: Order[];
  status: RouteStatus;
  total_time?: number;
  isClear?: boolean;
  start_latitude: number;
  start_longitude: number;
  end_latitude: number;
  end_longitude: number;
}
export interface DriverRoute {
  id: number;
  note: string;
}

const METERS_IN_MILE = 1609.34;

function roundToNearest30Minutes(roundedDate: Date) {
  const minutes = roundedDate.getMinutes();

  // Calculate the difference to the next 15-minute interval
  const remainder = minutes % 30;
  const difference = 30 - remainder;

  // Add the difference to the current time
  roundedDate.setMinutes(minutes + difference);

  return roundedDate;
}

const roundedTime = roundToNearest30Minutes(new Date());

let selectedTime = roundedTime.toISOString();

const defaultRoute: Route = {
  id: '0',
  start_time:
    format(Date.now(), 'yyyy-MM-dd') + selectedTime.slice(10, 19) + 'Z',
  start_location: '',
  end_location: '',
  orders: [],
  deliveries: [],
  status: 'new',
  start_latitude: 40.7596958,
  start_longitude: 73.9650045,
  end_latitude: 40.7596958,
  end_longitude: 73.9650045,
};

function getRequestData(route: Route) {
  return {
    ...route,
    driver: route.vehicle?.driver?.id,
    vehicle: route.vehicle?.id,
    orders: [],
    deliveries: route.deliveries.map((o) => o.id),
    // deliveries: route.orders.map((o) => o.id),
  };
}
function getRouteCreateData(route: Route) {
  return {
    ...route,
    id: undefined,
    orders: [],
    driver: route.vehicle?.driver?.id,
    vehicle: route.vehicle?.id,
    deliveries: route.deliveries.map((o) => o.id),
  };
}

async function getVehicles() {
  const {data} = await api.get<ListResponse<Vehicle>>('/vehicles/', {
    params: {
      limit: 100,
    },
  });
  return data;
}

async function getRouteById({queryKey}: QueryFunctionContext) {
  const [, routeId] = queryKey;
  if (routeId && routeId !== '0') {
    const {data} = await api.get<Route>(`/routes/${routeId}/`);
    return data;
  }

  return defaultRoute;
}

const toggleDeliveryGroupFlagMutation = async (body: {
  request_ids?: number[];
  is_urgent_delivery?: boolean;
}) => {
  if (!body.request_ids) {
    return;
  }
  const {data} = await api.post('/requests/requests/bulk-update/', body);
  return data;
};
export type SortOption = {
  value: string;
  label: string;
};

const sortOptions: SortOption[] = [
  {
    label: 'Most recent',
    value: 'most_recent',
  },
  {
    label: 'Alphabetical',
    value: 'alphabetical',
  },
];

type noteTypes = {
  [key: string]: object;
};

function useDeliveryPlanner() {
  const {search, pathname} = useLocation();
  const [sortBy, setSortBy] = useState<SortOption>(sortOptions[0]);
  const [showSendBulkMessageModal, setShowSendBulkMessageModal] =
    useState(false);
  const [customerIds, setCustomerIds] = useState<number[]>([]);
  const {push} = useHistory();
  const routeId =
    useMemo(() => new URLSearchParams(search).get('id'), [search]) ||
    sessionStorage.getItem('route_id') ||
    '0';

  const routeIdRef = useRef(routeId);
  const isInitial = useRef(true);

  useEffect(() => {
    if (routeIdRef.current !== '0') {
      push({search: `?id=${routeIdRef.current}`});
    }
  }, [push]);

  useEffect(() => {
    if (!search && routeIdRef.current !== '0') {
      if (!isInitial.current) {
        routeIdRef.current = '0';
        sessionStorage.setItem('route_id', '0');
      }
    }
    isInitial.current = false;
  }, [search]);

  const [startTime, setStartTime] = useState(new Date());
  const [filter, setFilter] = useState<FilterOption[]>([]);
  const [date, setDate] = useState<undefined>(undefined);
  const [searchKey, setSearch] = useState<string>('');
  const [preferenceTime, setPreferenceTime] = useState<
    undefined | {value: string; label: string}
  >(undefined);
  const [isSendRouteModalOpen, setIsSendRouteModalOpen] = useState(false);
  const [orderList, setOrderList] = useState<
    | (Order & {
        preference_date?: string;
        preference_time?: string;
        items?: any[];
      })[]
    | null
  >(null);
  const [activeOrder, setActiveOrder] = useState({});
  const [noteModal, setNoteModal] = useState(false);
  const [allChecked, setAllChecked] = useState(false);
  const [allCheckedAssign, setAllCheckedAssign] = useState(false);
  const [notes, setNotes] = useState<noteTypes>({});
  const [moveOrderModal, setMoveToOrder] = useState(false);
  const [showReverseConfirmModal, setShowReverseConfirmModal] = useState(false);
  const [disableSendRoute, setDisableSendRoute] = useState(false);
  const distance = useStore(useCallback((state) => state.distance, []));

  const queryClient = useQueryClient();
  const {data: vehicles} = useQuery('vehicles', getVehicles);

  const searchDebounced = useDebounce(searchKey, 1000);

  const {
    remove,
    orders: data,
    isLoading,
    hasNextPage,
    isFetching,
    count,
    fetchNextPage,
    refetch: refetchOrders,
  } = useOrderList({
    filter: filter,
    unAssignedOnly: true,
    preference_date: date,
    preference_time: preferenceTime
      ? preferenceTime.value.toLowerCase()
      : undefined,
    search: searchDebounced ? searchDebounced : undefined,
    sortBy: sortBy.value,
  });

  const {handleSubmit, control, register, setValue, reset} = useForm();

  const observer = useRef<IntersectionObserver>();
  const lastElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, fetchNextPage, hasNextPage]
  );

  const orderListRef = useRef<HTMLDivElement>(null);

  const [orders, setOrders] = useState(data);

  useEffect(() => {
    setOrders(data);
  }, [data]);

  const firstRender = useRef(true);

  useEffect(() => {
    if (!firstRender.current) {
      remove();
    }
    firstRender.current = false;
  }, [sortBy, remove]);

  let {data: route} = useQuery(['route', routeId], getRouteById, {
    initialData: defaultRoute,
    refetchOnWindowFocus: Boolean(routeId),
    refetchInterval: 0,
  });

  const {mutateAsync: toggleDeliveryGroupFlag} = useMutation(
    'toggle_delivery_group_flag',
    toggleDeliveryGroupFlagMutation,
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['orders']);
        toast.success('Successfully updated flag status!');
      },
      onError: (error) => {
        toast.error(
          get(error, 'response.data.error_message', 'Something went wrong')
        );
      },
    }
  );
  const handleChangeFilter = useCallback((value: FilterOption, meta: any) => {
    if (value) {
      setFilter((prev) =>
        prev.some((item) => item.label === value.label)
          ? reject(prev, value)
          : [...prev, value]
      );
    } else {
      setFilter([]);
    }
  }, []);

  const onChangeData = useCallback((value: any) => {
    setDate(value);
  }, []);

  const updateRoute = useMutation(
    // 'update_route',
    async function (nextRoute: Route) {
      const nextRouteData = getRequestData({
        ...route,
        ...nextRoute,
      });

      // save changes if route is already created
      if (route?.id && route?.id !== '0' && !nextRoute?.isClear) {
        const {data} = await api.patch<Route>(
          `/routes/${route?.id}/`,
          nextRouteData
        );
        return data;
      }

      // just return changes if route is not created
      return {
        ...route,
        ...nextRoute,
      };
    },

    {
      mutationKey: 'update_route',
      onMutate: async (nextRoute) => {
        const previousRoute = queryClient.getQueryData<Route>([
          'routes',
          routeId,
        ]);

        await queryClient.cancelQueries(['route', routeId]);

        queryClient.setQueryData(['route', routeId], {
          ...route,
          ...nextRoute,
        });

        return {previousRoute};
      },

      onSettled: (data) => {
        if (!data) {
          queryClient.invalidateQueries(['route', routeId]);
        }
      },
    }
  );

  const [selectedUnassignedOrders, setSelectedUnassignedOrders] = useState<
    number[]
  >([]);
  const [selectedAssignedOrders, setSelectedAssignedOrders] = useState<
    number[]
  >([]);

  const unassignedOrders = useMemo(
    function () {
      if (orders) {
        const routeOrders = route?.deliveries.map((o) => o.id) || [];

        const unsorted = [...orders].filter((o) => !routeOrders.includes(o.id));
        // .filter((el) => !el.processed)
        // .filter(filterOrder(filter));

        // if (sortBy) {
        //   return unsorted.sort(orderSortFunctionMap[sortBy.value]);
        // }

        return unsorted;
      }

      return [];
    },
    [orders, route?.deliveries]
  );

  const handleSendRoute = useCallback(
    async (updatedRoute: Route, sendMassMessage: boolean) => {
      await api.post(`/routes/${updatedRoute.id}/send/`, {
        send_preliminary_message: sendMassMessage,
      });
      push(pathname);
      sessionStorage.setItem('route_id', '0');

      defaultRoute.deliveries = [];

      updateRoute.mutate({
        ...updatedRoute,
        id: '0',
        status: 'new',
        deliveries: [],
        isClear: true,
        start_time: '',
      });

      setOrders(unassignedOrders);
      setSelectedAssignedOrders([]);
      refetchOrders();

      // queryClient.invalidateQueries(['orders']);
    },
    [pathname, push, refetchOrders, unassignedOrders, updateRoute]
  );

  // useEffect(
  //   function () {
  //     setSidebarExtended(false);
  //     setFullsizeContent(true);

  //     return function () {
  //       setSidebarExtended(true);
  //       setFullsizeContent(false);
  //     };
  //   },
  //   [setSidebarExtended, setFullsizeContent, route]
  // );

  function onChangeUnassignedCheckbox(
    id: number,
    checked: Boolean,
    customerId?: number
  ) {
    if (checked) {
      setSelectedUnassignedOrders([...selectedUnassignedOrders, id]);
      if (customerId) {
        setCustomerIds([...customerIds, customerId]);
      }
    } else {
      setSelectedUnassignedOrders(
        selectedUnassignedOrders.filter((selected) => selected !== id)
      );
      if (customerId) {
        setCustomerIds(customerIds.filter((id) => id !== customerId));
      }
    }
  }

  const [isFetchAll, setIsFetchAll] = useState(false);
  const [fetchingAll, setFetchingAll] = useState(false);

  const checkedAll = useRef(false);

  useEffect(() => {
    (async () => {
      if (isFetchAll && hasNextPage) {
        setFetchingAll(true);
        try {
          await fetchNextPage();
        } catch (error) {}
      } else {
        setFetchingAll(false);
      }
    })();

    if (checkedAll.current) {
      setSelectedUnassignedOrders(orders.map((item) => item.id));
    }
  }, [isFetchAll, orders, hasNextPage, fetchNextPage]);

  useEffect(() => {
    if (route) {
      if (route.deliveries.length < 1) {
        setAllCheckedAssign(false);
      }
      if (unassignedOrders.length < 1) {
        setAllChecked(false);
      }
    }
  }, [route, fetchNextPage, unassignedOrders.length]);

  async function onChangeUnassignedCheckboxAll(
    ids: number[],
    checked: boolean
  ) {
    if (checked) {
      setSelectedUnassignedOrders(ids);
    } else {
      setSelectedUnassignedOrders([]);
    }

    checkedAll.current = checked;
    setIsFetchAll(true);
  }

  function onChangeAssignedCheckbox(id: number, checked: boolean) {
    if (checked) {
      setSelectedAssignedOrders([...selectedAssignedOrders, id]);
    } else {
      setSelectedAssignedOrders(
        selectedAssignedOrders.filter((selected) => selected !== id)
      );
    }
  }

  function onChangeAssignedCheckboxAll(ids: number[], checked: boolean) {
    if (checked) {
      setSelectedAssignedOrders(ids);
    } else {
      setSelectedAssignedOrders([]);
    }
  }

  function onChangeRouteParams(
    start_location: {
      name: string;
      lat: number;
      lng: number;
    },
    end_location: {
      name: string;
      lat: number;
      lng: number;
    },
    currentVehicle?: Vehicle
  ) {
    if (!route) return;

    updateRoute.mutate({
      ...route,
      start_location: start_location.name,
      start_latitude: start_location.lat,
      start_longitude: start_location.lng,
      end_location: end_location.name,
      end_latitude: end_location.lat,
      end_longitude: end_location.lng,
      start_time: startTime ? formatISO(startTime) : undefined,
      vehicle: currentVehicle,
    });
    defaultRoute.start_location = start_location.name;
    defaultRoute.start_latitude = start_location.lat;
    defaultRoute.start_longitude = start_location.lng;
    defaultRoute.end_location = end_location.name;
    defaultRoute.end_latitude = end_location.lat;
    defaultRoute.end_longitude = end_location.lng;
    defaultRoute.vehicle = currentVehicle;
    defaultRoute.start_time = startTime ? formatISO(startTime) : undefined;

    setDisableSendRoute(true);
  }

  function onChangeStartTime(value: Date) {
    if (!route) return;
    setStartTime(value);

    updateRoute.mutate({
      ...route,
      start_time: value ? formatISO(value) : undefined,
    });
    defaultRoute.start_time = value ? formatISO(value) : undefined;
    setDisableSendRoute(true);
  }

  function assignSelected() {
    if (!route) return;

    updateRoute.mutate({
      ...route,
      deliveries: [
        ...route.deliveries,
        ...unassignedOrders.filter((o) =>
          selectedUnassignedOrders.includes(o.id)
        ),
      ],
    });

    defaultRoute.deliveries = [
      ...route.deliveries,
      ...unassignedOrders.filter((o) =>
        selectedUnassignedOrders.includes(o.id)
      ),
    ];

    if (hasNextPage) {
      fetchNextPage();
    }

    setSelectedUnassignedOrders([]);
  }

  async function moveToOrders() {
    if (!route) return;

    try {
      await api.post(`/routes/deliveries/move-to-orders/`, {
        order_ids: selectedUnassignedOrders,
      });
      setMoveToOrder(false);
      setOrders((pr) =>
        pr.filter((item) => !selectedUnassignedOrders.includes(item.id))
      );
      setSelectedUnassignedOrders([]);
    } catch (error) {}
  }

  function unassignSelected() {
    if (!route) return;

    defaultRoute.deliveries = route?.deliveries.filter(
      (o) => !selectedAssignedOrders.includes(o.id)
    );
    updateRoute.mutate({
      ...route,
      deliveries: route?.deliveries.filter(
        (o) => !selectedAssignedOrders.includes(o.id)
      ),
    });

    setSelectedAssignedOrders([]);
  }

  const resetCalculate = () => {
    push({search: ''});
    sessionStorage.setItem('route_id', '0');
    if (route)
      updateRoute.mutate({
        ...route,
        deliveries: [],
        status: 'new',
      });
    defaultRoute.deliveries = [];
  };

  async function sendDriverNote(driverNote: DriverRoute) {
    return await api.post(`/routes/deliveries/staff_note/${driverNote.id}/`, {
      staff_note: driverNote.note,
    });
  }

  const [calculating, setCalculating] = useState(false);

  async function calculate({
    optimizeRoute = false,
  }: {optimizeRoute?: boolean} = {}) {
    let id = route?.id;

    if (!route?.start_location) {
      return toast.error(
        'Address or vehicle was not assigned, Please assign address and vehicle!'
      );
    }
    // if route is not created, create it
    if (route && (!route.id || route.id === '0')) {
      if (route?.deliveries.length >= 40) {
        setCalculating(true);
      }

      try {
        const {data} = await api.post<Route>(
          '/routes/',
          getRouteCreateData(route)
        );
        id = data.id;
      } catch (error) {
        toast.error('Error while creating route, Please try again!');
      }
    }
    if (id === '0') {
      return;
    }

    const delivery_ids = route?.deliveries.map((order) =>
      String(get(order, 'id', ''))
    );

    // initiate task to calculate the route
    try {
      await api.post(`/routes/${id}/calculate/`, {
        optimize_route: optimizeRoute,
        delivery_ids,
        start_location: route?.start_location,
        end_location: route?.end_location,
        start_time: route.start_time,
        start_latitude: route.start_latitude,
        start_longitude: route.start_longitude,
        end_latitude: route.end_latitude,
        end_longitude: route.end_longitude,
        vehicle: route.vehicle?.id,
      });
    } catch (error) {
      toast.error('Error, Please try again!');
      setCalculating(false);
    }

    let pendingRoute = route;

    do {
      await wait(5000);
      try {
        const {data} = await api.get<Route>(`/routes/${id}/`);
        pendingRoute = data;

        alertUser(data.status, get(data, 'error_message', ''));
        if (data.status === 'processed') {
          updateRoute.mutate({...data, isClear: true});
          setCalculating(false);
          setDisableSendRoute(false);
        }
      } catch (error) {
        setCalculating(false);
      }
    } while (pendingRoute.status === 'processing');

    if (id) {
      defaultRoute.deliveries = [];
      push({search: `?id=${id}`});
      routeIdRef.current = String(id);
      sessionStorage.setItem('route_id', String(id));
    }
  }

  function alertUser(status: string, message: string) {
    if (status === 'cancelled') {
      setCalculating(false);
      setDisableSendRoute(true);
      toast.error(message || 'Wrong address, Please check attached addresses!');
    }
  }
  function statsCalculator(route: undefined | Route) {
    return {
      stops: route?.deliveries.length,
      distance: `${Math.round((distance * 10) / METERS_IN_MILE) / 10} mi`,
      total_time: route?.total_time
        ? `${Math.floor(route.total_time / 60)}h ${route.total_time % 60}m`
        : '',
      end_time:
        route?.start_time && route.total_time
          ? format(
              add(parseISO(route.start_time), {minutes: route.total_time}),
              'h:mm aaa'
            )
          : '-:-- --',
    };
  }

  const reorder = (list: Order[], startIndex: number, endIndex: number) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const move = (
    source: any,
    destination: any,
    droppableSource: any,
    droppableDestination: any
  ) => {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination.index, 0, removed);

    const result = {
      assignedDeliveries: [],
      inbox: [],
    };
    //@ts-ignore
    result[droppableSource.droppableId] = sourceClone;
    //@ts-ignore
    result[droppableDestination.droppableId] = destClone;

    return result;
  };

  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;

    if (!route) return;

    if (result.source.droppableId === result.destination.droppableId) {
      if (result.source.droppableId === 'assignedDeliveries') {
        const newItems = [...route.deliveries];
        const [reorderedItem] = newItems.splice(result.source.index, 1);
        newItems.splice(result.destination.index, 0, reorderedItem);
        defaultRoute.deliveries = newItems;
        route.deliveries = newItems;
        await updateRoute.mutateAsync({
          ...route,
          deliveries: newItems,
        });
        setDisableSendRoute(true);
      } else {
        const res = reorder(
          unassignedOrders,
          result.source.index,
          result.destination.index
        );
        unassignedOrders.length = 0;
        unassignedOrders.push(...res);
      }
    } else {
      const res = move(
        result.source.droppableId === 'assignedDeliveries'
          ? route.deliveries
          : unassignedOrders,
        result.destination.droppableId === 'assignedDeliveries'
          ? route.deliveries
          : unassignedOrders,
        result.source,
        result.destination
      );
      defaultRoute.deliveries = res?.assignedDeliveries;
      route.deliveries = res?.assignedDeliveries;
      await updateRoute.mutateAsync({
        ...route,
        deliveries: res?.assignedDeliveries,
      });
      unassignedOrders.length = 0;
      unassignedOrders.push(...res.inbox);
      setSelectedUnassignedOrders((pr) =>
        pr.filter((item) => item !== Number(result.draggableId))
      );
      setSelectedAssignedOrders((pr) =>
        pr.filter((item) => item !== Number(result.draggableId))
      );
      setDisableSendRoute(true);
    }
  };

  const handleOrdersClick =
    (order: Order) => (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      setActiveOrder(order);
      setOrderList(order.orders);
    };

  const handleOrdersClose = () => {
    setOrderList(null);
    setNoteModal(false);
  };

  const handleNoteClick = (id: number, staff: string) => {
    setNoteModal(true);
    setValue('driverNote', staff ? staff : get(notes[`${id}`], 'note', ''));
  };

  const noteSubmit = async (value: any) => {
    const body = {
      id: Number(get(activeOrder, 'id', '')),
      note: String(value?.driverNote),
    };
    const res = await sendDriverNote(body);
    if (res.status === 201) {
      setNotes((pr) => ({...pr, [`${get(activeOrder, 'id', '')}`]: body}));
      reset();
      setNoteModal(false);
      // refetch();
    }
  };

  const onChangeCheckbox = () => {
    setAllChecked((pr) => !pr);
    onChangeUnassignedCheckboxAll(
      unassignedOrders.map((item) => item.id),
      !allChecked
    );
  };
  const onChangeCheckboxAssign = () => {
    setAllCheckedAssign((pr) => !pr);
    if (route) {
      onChangeAssignedCheckboxAll(
        route.deliveries.map((item) => item.id),
        !allCheckedAssign
      );
    }
  };

  const onChange = (val: any) => {
    onChangeUnassignedCheckboxAll([], false);
    setAllChecked(false);
    setPreferenceTime(val);
  };

  const reverseSequence = useCallback(async () => {
    if (!route) return;
    try {
      const newArray = [...route?.deliveries];
      const reversedDeliveries = newArray.reverse();
      route.deliveries = reversedDeliveries;
      defaultRoute.deliveries = reversedDeliveries;
      setDisableSendRoute(true);
      setShowReverseConfirmModal(false);
    } catch (error) {
      console.log(error);
      toast.error(
        'Error while reversing the sequence of deliveries, Please try again!'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route]);

  const isSubmittable = useMemo(() => {
    return Boolean(
      route &&
        route.start_location &&
        route.end_location &&
        route.start_time &&
        !!route?.vehicle
    );
  }, [route]);

  return {
    route: route ? route : defaultRoute,
    currentVehicle: vehicles?.results.find((v) => v.id === route?.vehicle?.id),
    vehicles,
    unassignedOrders,
    isLoading,
    selectedUnassignedOrders,
    selectedAssignedOrders,

    stats: statsCalculator(route),

    isSubmittable,
    disableSendRoute,
    onChangeUnassignedCheckbox,
    onChangeAssignedCheckbox,
    onChangeRouteParams,
    onChangeStartTime,
    onSendRoute: handleSendRoute,
    count: count - (route?.deliveries.length || 0),
    sortBy,
    setSortBy,
    filter,
    onFilterChange: handleChangeFilter,
    onChangeData,
    assignSelected,
    moveToOrders,
    unassignSelected,
    calculate,
    date,
    preferenceTime,
    isFetching,
    roundedTime: route?.start_time
      ? new Date(parseJSON(route?.start_time)).setHours(0, 0, 0, 0) >
        new Date().setHours(0, 0, 0, 0)
        ? undefined
        : roundedTime
      : roundedTime,
    setSearch,
    searchKey,
    fetchingAll,
    calculating,
    resetCalculate,
    sortOptions,
    search,
    handleDragEnd,
    orderList,
    setActiveOrder,
    handleOrdersClick,
    handleOrdersClose,
    handleNoteClick,
    noteSubmit,
    onChangeCheckbox,
    onChangeCheckboxAssign,
    onChange,
    handleSubmit,
    control,
    register,
    noteModal,
    allChecked,
    allCheckedAssign,
    isSendRouteModalOpen,
    setIsSendRouteModalOpen,
    lastElementRef,
    orderListRef,
    setMoveToOrder,
    moveOrderModal,
    showReverseConfirmModal,
    setShowReverseConfirmModal,
    reverseSequence,
    toggleDeliveryGroupFlag,
    showSendBulkMessageModal,
    setShowSendBulkMessageModal,
    customerIds,
  };
}

export default useDeliveryPlanner;
